import React, { useEffect, useRef, useState } from 'react';
import { FiPlay, FiStar, FiArrowDown } from 'react-icons/fi';
import { useReservation } from '../contexts/ReservationContext';
import './Hero.css';

// Séquence vidéo du hero (sources libres de droits Pexels, ré-encodées et hébergées sur le site).
// Chaque clip existe en 1080p (desktop) et 540p (mobile).
const HERO_CLIPS = [
  'hero-rolls-royce-route',
  'hero-cliente-descend-limousine',
  'hero-chauffeur-ouvre-porte'
];

const clipSrc = (name, mobile) => `/videos/${name}${mobile ? '-540p' : ''}.mp4`;

const Hero = () => {
  const { open } = useReservation();

  // Vidéo : deux lecteurs superposés, l'un joue pendant que l'autre précharge le clip suivant.
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [activeSlot, setActiveSlot] = useState(0);
  const [slotClips, setSlotClips] = useState([0, 1 % HERO_CLIPS.length]);
  const videoRefs = [useRef(null), useRef(null)];

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const conn = navigator.connection || {};
    const lowData = conn.saveData || /(^|[^3-5])2g/.test(conn.effectiveType || '');
    setIsMobile(window.innerWidth <= 768);
    if (!reduceMotion && !lowData) setVideoEnabled(true);
  }, []);

  useEffect(() => {
    if (!videoEnabled) return;
    const first = videoRefs[0].current;
    if (first) {
      const p = first.play();
      if (p && p.catch) p.catch(() => setVideoEnabled(false));
    }
  }, [videoEnabled]);

  const handleEnded = (slot) => {
    const nextSlot = 1 - slot;
    const nextVideo = videoRefs[nextSlot].current;
    if (nextVideo) {
      nextVideo.currentTime = 0;
      const p = nextVideo.play();
      if (p && p.catch) p.catch(() => {});
    }
    setActiveSlot(nextSlot);
    setSlotClips((prev) => {
      const next = [...prev];
      next[slot] = (prev[nextSlot] + 1) % HERO_CLIPS.length;
      return next;
    });
  };
  const scrollToReservation = () => open();

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="hero">
      {/* Image de fond avec overlay */}
      <div className="hero-background">
        <div className="hero-overlay"></div>
        <picture>
          <source srcSet="/images/hero/hero-limousines-mariage-paris.webp" type="image/webp" />
          <img
            src="/images/hero/hero-limousines-mariage-paris.webp"
            alt="Flotte de limousines de mariage VIP Limousine Paris — Chrysler 300 stretch, Excalibur Phantom cabriolet vintage et Hummer H3 stretch blanche, location de limousine avec chauffeur en Île-de-France"
            className="hero-image"
            width="1920"
            height="1280"
            fetchpriority="high"
            loading="eager"
          />
        </picture>

        {videoEnabled && (
          <div className={`hero-video-layer ${playing ? 'is-playing' : ''}`} aria-hidden="true">
            {[0, 1].map((slot) => (
              <video
                key={slot}
                ref={videoRefs[slot]}
                className={`hero-video ${activeSlot === slot ? 'is-active' : ''}`}
                src={clipSrc(HERO_CLIPS[slotClips[slot]], isMobile)}
                muted
                playsInline
                preload="auto"
                disablePictureInPicture
                onPlaying={() => setPlaying(true)}
                onEnded={() => handleEnded(slot)}
                onError={() => setVideoEnabled(false)}
                tabIndex={-1}
              />
            ))}
          </div>
        )}
      </div>

      <div className="hero-content">
        <div className="container">
          <div className="hero-text">
            <div className="hero-badge">
              <FiStar className="badge-icon" />
              <span>Service Premium depuis 2010</span>
            </div>
            
            <h1 className="hero-title">
              <span className="title-main">Limousine</span>
              <span className="title-accent">Paris</span>
              <span className="title-sub">Service VIP · Transport de luxe avec chauffeur privé</span>
            </h1>
            
            <p className="hero-description">
              Découvrez le summum du transport de luxe avec notre flotte de limousines premium. 
              Service personnalisé avec chauffeur professionnel pour tous vos événements d'exception.
            </p>
            
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Clients satisfaits</span>
              </div>
              <div className="stat">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Service disponible</span>
              </div>
              <div className="stat">
                <span className="stat-number">15</span>
                <span className="stat-label">Véhicules de luxe</span>
              </div>
            </div>
            
            <div className="hero-actions">
              <button 
                onClick={scrollToReservation}
                className="btn btn-primary hero-cta"
              >
                Réserver maintenant
              </button>
              
              <button 
                onClick={scrollToServices}
                className="btn btn-outline hero-secondary"
              >
                <FiPlay className="btn-icon" />
                Découvrir nos services
              </button>
            </div>
          </div>
        </div>
        
        <button 
          onClick={scrollToServices}
          className="scroll-indicator"
          aria-label="Faire défiler vers le bas"
        >
          <FiArrowDown />
        </button>
      </div>
      
      {/* Éléments décoratifs */}
      <div className="hero-decorations">
        <div className="decoration decoration-1"></div>
        <div className="decoration decoration-2"></div>
        <div className="decoration decoration-3"></div>
      </div>
    </section>
  );
};

export default Hero;
